<template>
  <Modal
    ref="modal"
    :full-screen="true"
    :close-button="false"
    keep-content
  >
    <div v-if="loading" class="loading-absolute-center"></div>
    <template v-else>
      <TemplateHeader
        :workspace="workspace"
        :template="selectedTemplate"
        :category="selectedTemplateCategory"
        :installing="installing"
        :installed="isSelectedTemplateInstalled"
        @use-template="openTemplateConfirm"
      ></TemplateHeader>
      <template v-if="hasCategories">
        <TemplateCategories
          ref="categories"
          :categories="categories"
          :selected-template="selectedTemplate"
          @selected="selectTemplate"
        >
          <div class="modal__actions">
            <a class="modal__close" @click="hide()">
              <i class="iconoir-cancel"></i>
            </a>
          </div>
        </TemplateCategories>

        <section v-if="selectedTemplate" class="templates__catalog-body">
          <div class="templates__catalog-card">
            <div class="templates__catalog-card-header">
              <div class="templates__catalog-icon">
                <i :class="selectedTemplate.icon"></i>
              </div>
              <div>
                <p
                  v-if="selectedTemplateCategory"
                  class="templates__catalog-eyebrow"
                >
                  {{ selectedTemplateCategory.name }}
                </p>
                <h2>{{ selectedTemplate.name }}</h2>
              </div>
            </div>

            <p class="templates__catalog-description">
              {{ templateDescription }}
            </p>

            <div class="templates__catalog-summary-grid">
              <div class="templates__catalog-summary-item">
                <span>{{ $t('templateConfirmModal.categoryLabel') }}</span>
                <strong>{{
                  selectedTemplateCategory?.name ||
                  $t('templateModal.descriptionFallback')
                }}</strong>
              </div>
              <div class="templates__catalog-summary-item">
                <span>{{ $t('templateConfirmModal.includedTitle') }}</span>
                <strong>{{ formattedTableCount }}</strong>
              </div>
            </div>

            <div class="templates__catalog-section">
              <h3>{{ $t('templateModal.contentsTitle') }}</h3>
              <ul
                v-if="templateContentItems.length > 0"
                class="templates__catalog-list"
              >
                <li
                  v-for="(contentItem, index) in templateContentItems"
                  :key="`${contentItem}-${index}`"
                >
                  <i class="iconoir-check-circle"></i>
                  <span>{{ contentItem }}</span>
                </li>
              </ul>
              <p v-else class="templates__catalog-muted">
                {{ $t('templateModal.contentsFallback') }}
              </p>
            </div>

            <div class="templates__catalog-section">
              <h3>{{ $t('templateConfirmModal.operationalViewsTitle') }}</h3>
              <ul
                v-if="supportedOperationalViews.length > 0"
                class="templates__catalog-list"
              >
                <li
                  v-for="(view, index) in supportedOperationalViews"
                  :key="`${view.type}-${view.label}-${index}`"
                >
                  <i :class="operationalViewIcon(view.type)"></i>
                  <span>
                    {{ operationalViewTypeLabel(view.type) }} — {{ view.label }}
                  </span>
                </li>
              </ul>
              <p v-else class="templates__catalog-muted">
                {{ $t('templateModal.noOperationalViews') }}
              </p>
            </div>

            <div class="templates__catalog-actions">
              <Button
                type="primary"
                :loading="installing"
                :disabled="installing"
                @click="openTemplateConfirm"
              >
                {{
                  isSelectedTemplateInstalled
                    ? $t('templateConfirmModal.openInstalled')
                    : $t('templateHeader.use')
                }}
              </Button>
            </div>
          </div>
        </section>
      </template>
      <div v-else class="templates__empty">
        <div class="modal__actions">
          <a class="modal__close" @click="hide()">
            <i class="iconoir-cancel"></i>
          </a>
        </div>
        <div class="templates__empty-content">
          <i class="templates__empty-icon iconoir-database"></i>
          <h2>{{ $t('templateModal.emptyTitle') }}</h2>
          <p>{{ $t('templateModal.emptyDescription') }}</p>
          <Button
            v-if="workspace"
            class="templates__empty-action"
            :loading="syncingVokalisTemplates"
            :disabled="syncingVokalisTemplates"
            @click="syncVokalisTemplates"
          >
            {{ $t('templateModal.syncTemplates') }}
          </Button>
        </div>
      </div>
    </template>
    <TemplateUseConfirmation
      ref="useConfirmation"
      :workspace="workspace"
      @install-started="handleInstallStarted"
      @installed="refreshAfterInstall"
      @deleted="refreshAfterDelete"
      @opened="hide()"
    />
  </Modal>
</template>

<script>
import modal from '@baserow/modules/core/mixins/modal'
import TemplateService from '@baserow/modules/core/services/template'
import { notifyIf } from '@baserow/modules/core/utils/error'

import TemplateHeader from '@baserow/modules/core/components/template/TemplateHeader'
import TemplateCategories from '@baserow/modules/core/components/template/TemplateCategories'
import TemplateUseConfirmation from '@baserow/modules/core/components/template/TemplateUseConfirmation'

const SUPPORTED_OPERATIONAL_VIEW_TYPES = ['kanban', 'calendar', 'timeline']

export default {
  name: 'TemplateModal',
  components: {
    TemplateHeader,
    TemplateCategories,
    TemplateUseConfirmation,
  },
  mixins: [modal],
  props: {
    workspace: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      installing: false,
      syncingVokalisTemplates: false,
      categories: [],
      selectedTemplate: null,
      selectedTemplateCategory: null,
    }
  },
  computed: {
    hasCategories() {
      return this.categories.length > 0
    },
    features() {
      return this.selectedTemplate?.features || {}
    },
    tableCount() {
      const tableNames = this.features.table_names
      const derivedCount = Array.isArray(tableNames) ? tableNames.length : 0
      return Number(this.features.table_count) || derivedCount
    },
    formattedTableCount() {
      if (this.tableCount === 1) {
        return this.$t('dashboard.templateTableCountOne')
      }

      return this.$t('dashboard.templateTableCountMany', {
        count: this.tableCount,
      })
    },
    templateSlug() {
      return this.selectedTemplate?.slug || ''
    },
    templateDescription() {
      const modalKey = `templateModal.templateDescriptions.${this.templateSlug}`
      const dashboardKey = `dashboard.templateDescriptions.${this.templateSlug}`

      if (this.templateSlug && this.$te(modalKey)) {
        return this.$t(modalKey)
      }

      if (this.templateSlug && this.$te(dashboardKey)) {
        return this.$t(dashboardKey)
      }

      return (
        this.selectedTemplate?.description ||
        this.$t('templateModal.descriptionFallback')
      )
    },
    templateContentItems() {
      const baseKey = `templateModal.templateContents.${this.templateSlug}`
      const items = []

      if (this.templateSlug) {
        for (let index = 0; this.$te(`${baseKey}.${index}`); index++) {
          items.push(this.$t(`${baseKey}.${index}`))
        }
      }

      return items
    },
    isSelectedTemplateInstalled() {
      return Boolean(
        this.selectedTemplate?.is_installed &&
        this.selectedTemplate?.installation
      )
    },
    supportedOperationalViews() {
      const views = this.features.operational_views
      if (!Array.isArray(views)) {
        return []
      }

      return views.filter(
        (view) =>
          view?.type &&
          view?.label &&
          SUPPORTED_OPERATIONAL_VIEW_TYPES.includes(view.type)
      )
    },
  },
  methods: {
    /**
     * When the modal is opened we want to fetch all the templates and their
     * categories. They will be placed in the sidebar so that the user can select a
     * template from a clean Vokalis catalog.
     */
    async show(templateId = null, ...args) {
      modal.methods.show.call(this, ...args)

      this.loading = true
      this.installing = false
      this.categories = []
      this.selectedTemplate = null
      this.selectedTemplateCategory = null

      try {
        await this.reloadCategories()
        this.loading = false
      } catch (error) {
        notifyIf(error, 'templates')
        this.hide()
        return
      }

      this.selectTemplateAfterCategoriesReload(templateId)
    },
    handleInstallStarted() {
      this.installing = true
      this.hide()
    },
    async reloadCategories() {
      const { data } = await TemplateService(this.$client).fetchAll(
        this.workspace.id
      )
      this.categories = this.filterVokalisTemplateCategories(data)
    },
    selectTemplateAfterCategoriesReload(templateId = null) {
      const selected = this.findTemplateToSelect(templateId)
      if (selected === null) {
        return
      }

      this.$nextTick(() => {
        if (this.$refs.categories) {
          this.$refs.categories.selectCategory(selected.category.id)
        }
        this.selectTemplate(selected)
      })
    },
    async syncVokalisTemplates() {
      if (this.syncingVokalisTemplates) {
        return
      }

      this.syncingVokalisTemplates = true
      try {
        const { data } = await TemplateService(this.$client).syncVokalis(
          this.workspace.id
        )
        this.categories = this.filterVokalisTemplateCategories(data)
        this.selectTemplateAfterCategoriesReload()
      } catch (error) {
        notifyIf(error, 'templates')
      } finally {
        this.syncingVokalisTemplates = false
      }
    },
    async refreshAfterInstall() {
      this.installing = false
      try {
        await this.reloadCategories()
      } catch (error) {
        notifyIf(error, 'templates')
      }
      this.hide()
    },
    async refreshAfterDelete() {
      const templateId = this.selectedTemplate?.id || null
      await this.reloadCategories()
      this.selectTemplateAfterCategoriesReload(templateId)
    },
    filterVokalisTemplateCategories(categories) {
      return categories
        .map((category) => ({
          ...category,
          templates: (category.templates || []).filter((template) =>
            this.isVokalisTemplate(template)
          ),
        }))
        .filter((category) => category.templates.length > 0)
    },
    isVokalisTemplate(template) {
      const keywords = Array.isArray(template.keywords)
        ? template.keywords
        : String(template.keywords || '').split(',')

      return keywords
        .map((keyword) => String(keyword).trim().toLowerCase())
        .includes('vokalis')
    },
    findTemplateToSelect(templateId) {
      let firstTemplate = null

      for (let i = 0; i < this.categories.length; i++) {
        const category = this.categories[i]
        for (let i2 = 0; i2 < category.templates.length; i2++) {
          const template = category.templates[i2]
          const templateSelection = { template, category }

          if (firstTemplate === null) {
            firstTemplate = templateSelection
          }

          if (
            (templateId === null && template.is_default) ||
            (templateId !== null &&
              (template.id === templateId || template.slug === templateId))
          ) {
            return templateSelection
          }
        }
      }

      return firstTemplate
    },
    selectTemplate({ template, category }) {
      this.selectedTemplate = template
      this.selectedTemplateCategory = category
    },
    openTemplateConfirm() {
      if (this.installing) {
        return
      }

      this.$nextTick(() => {
        if (this.selectedTemplate && this.$refs.useConfirmation) {
          this.$refs.useConfirmation.show(
            this.selectedTemplate,
            this.selectedTemplateCategory
          )
        }
      })
    },
    operationalViewTypeLabel(type) {
      const key = `templateHeader.operationalViewTypes.${type}`
      return this.$te(key) ? this.$t(key) : type
    },
    operationalViewIcon(type) {
      const icons = {
        kanban: 'iconoir-kanban-board',
        calendar: 'iconoir-calendar',
        timeline: 'iconoir-timeline',
      }
      return icons[type] || 'iconoir-view-grid'
    },
    async hide(...args) {
      modal.methods.hide.call(this, ...args)
      this.selectedTemplate = null
    },
  },
}
</script>
